import { Engine as BABYLON_Engine } from '@inter-ikea-betrakta-babylonjs/core/Engines/engine.js';
import { RenderTargetTexture as BABYLON_RenderTargetTexture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/renderTargetTexture.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
// Side effect
import './shaders/accumulation_buffer.fragment.js';
import { BloomEffect } from './Bloom.js';
import { PostProcessShader, ShaderDescription, ShaderDescriptorType } from './utility/PostProcessShader.js';
var JitterSampleType;
(function (JitterSampleType) {
    JitterSampleType[JitterSampleType["helix_4"] = 1] = "helix_4";
    JitterSampleType[JitterSampleType["halton_2_3_16"] = 2] = "halton_2_3_16";
})(JitterSampleType || (JitterSampleType = {}));
const FEATURE_MASK_TONEMAPPING = 1;
const FEATURE_MASK_BLOOM = 2;
const FEATURE_MASK_LUT = 4;
export class AccumulationBuffer {
    constructor(scene, accumulation_frame_count, framebuffer) {
        this._accumulator_shader_feature_mask = 0;
        /**
         * Number of frames to render before we start the accumulation process.
         * Useful for the AO system which has its own accumulator that might need some extra frames from time to time.
         */
        this._queued_warmup_frames = 0;
        this._scene = scene;
        this._accumulation_frame_count = accumulation_frame_count;
        this._framebuffer = framebuffer;
        this._accumulation_index = 0;
        this._jitter_index = 0;
        this._jitter_samples = AccumulationBuffer.get_jitter_samples(JitterSampleType.halton_2_3_16, this._accumulation_frame_count);
        const width = framebuffer.get_width();
        const height = framebuffer.get_height();
        // Since we accumulate after tonemapping, RGBA8 texture is enough
        const type = BABYLON_Engine.TEXTURETYPE_UNSIGNED_BYTE;
        // Need to use NEAREST sampling mode in case we have not power of two texture sizes, and also because we want unfiltered texture lookups
        this.accumulation_buffer1 = new BABYLON_RenderTargetTexture('acc1', { width: width, height: height }, scene, undefined, undefined, type, undefined, BABYLON_Texture.NEAREST_SAMPLINGMODE, false, false, false, BABYLON_Engine.TEXTUREFORMAT_RGBA);
        this.accumulation_buffer2 = new BABYLON_RenderTargetTexture('acc2', { width: width, height: height }, scene, undefined, undefined, type, undefined, BABYLON_Texture.NEAREST_SAMPLINGMODE, false, false, false, BABYLON_Engine.TEXTUREFORMAT_RGBA);
        // This is needed for offset viewports to work properly with the temporal stack
        this.accumulation_buffer1.ignoreCameraViewport = true;
        this.accumulation_buffer2.ignoreCameraViewport = true;
        const { shader, descriptors } = this.create_accumulator_shader(0);
        this._shader = shader;
        this._descriptors = descriptors;
        this.reset_stable_frames();
    }
    create_accumulator_shader(feature_mask) {
        // Note: You may think it's a good idea to just use the PostProcessShader's functionality for hot-swapping defines, instead of re-creating it.
        // BUT, depending on bloom we can have different sampler counts, meaning we can get different descriptors, meaning we need to re-create.
        this._accumulator_shader_feature_mask = feature_mask;
        const enable_tonemapping = (feature_mask & FEATURE_MASK_TONEMAPPING) !== 0;
        const enable_bloom = (feature_mask & FEATURE_MASK_BLOOM) !== 0;
        const enable_lut = (feature_mask & FEATURE_MASK_LUT) !== 0;
        const bloom_texture_names = [];
        if (enable_bloom) {
            for (let i = 0; i < BloomEffect.BLOOM_LEVELS_COUNT; i++) {
                bloom_texture_names.push('bloomSampler[' + i + ']');
            }
        }
        const defines = [];
        if (enable_bloom) {
            defines.push('#define ENABLE_BLOOM');
            defines.push('#define BLOOM_LEVELS_COUNT ' + BloomEffect.BLOOM_LEVELS_COUNT);
        }
        if (enable_tonemapping) {
            defines.push('#define ENABLE_TONEMAPPING');
        }
        const shader_description = new ShaderDescription('accumulation_buffer');
        const bloom_sampler_descriptors = [];
        for (let i = 0; i < bloom_texture_names.length; i++) {
            bloom_sampler_descriptors.push(shader_description.add_descriptor(ShaderDescriptorType.SAMPLER_2D, bloom_texture_names[i]));
        }
        if (enable_lut) {
            defines.push('#define ENABLE_LUT');
        }
        const descriptors = {
            current_sampler: shader_description.add_descriptor(ShaderDescriptorType.SAMPLER_2D, 'textureSampler'),
            previous_sampler: shader_description.add_descriptor(ShaderDescriptorType.SAMPLER_2D, 'previousFrames'),
            accumulation_blend: shader_description.add_descriptor(ShaderDescriptorType.FLOAT, 'accumulationBlend'),
            bloom_samplers: bloom_sampler_descriptors,
            bloom_intensity: shader_description.add_descriptor(ShaderDescriptorType.FLOAT, 'bloomIntensity'),
            lut_sampler: shader_description.add_descriptor(ShaderDescriptorType.SAMPLER_2D, 'lutSampler')
        };
        const shader = new PostProcessShader(this._scene, shader_description, defines);
        this._shader = shader;
        this._descriptors = descriptors;
        // Set default values for descriptors we might not initialize when rendering to avoid warnings
        this._shader.set_descriptor(this._descriptors.lut_sampler, null);
        this._shader.set_descriptor(this._descriptors.bloom_intensity, 0.0);
        for (let i = 0; i < bloom_sampler_descriptors.length; i++) {
            this._shader.set_descriptor(bloom_sampler_descriptors[i], null);
        }
        return { shader: shader, descriptors: descriptors };
    }
    render(bloom, lut, tonemap) {
        const feature_mask = (bloom !== null ? FEATURE_MASK_BLOOM : 0) |
            (tonemap ? FEATURE_MASK_TONEMAPPING : 0) |
            ((lut !== null && lut.lut_texture !== null) ? FEATURE_MASK_LUT : 0);
        // If the feature mask has changed, or the number of bloom samplers, we need to re-compile.
        if (this._accumulator_shader_feature_mask !== feature_mask ||
            (bloom !== null && bloom.result_textures.length !== this._descriptors.bloom_samplers.length)) {
            this._shader.dispose();
            this.create_accumulator_shader(feature_mask);
        }
        this._shader.set_descriptor(this._descriptors.current_sampler, this._framebuffer.get_texture());
        this._shader.set_descriptor(this._descriptors.previous_sampler, this.accumulation_buffer2);
        this._shader.set_descriptor(this._descriptors.accumulation_blend, 1.0 / Math.max(1.0, 1.0 + this._accumulation_index));
        if (bloom !== null) {
            this._shader.set_descriptor(this._descriptors.bloom_intensity, bloom.intensity);
            for (let i = 0; i < this._descriptors.bloom_samplers.length; i++) {
                this._shader.set_descriptor(this._descriptors.bloom_samplers[i], bloom.result_textures[i]);
            }
        }
        if (lut !== null) {
            this._shader.set_descriptor(this._descriptors.lut_sampler, lut.lut_texture);
        }
        // accumulate (and optionally tonemap and add bloom)
        this._shader.render(this.accumulation_buffer1);
        // ping pong buffers
        this.flip_flop();
    }
    flip_flop() {
        const temp = this.accumulation_buffer1;
        this.accumulation_buffer1 = this.accumulation_buffer2;
        this.accumulation_buffer2 = temp;
    }
    resize(width, height) {
        const size = {
            width: width,
            height: height
        };
        this.accumulation_buffer1.resize(size);
        this.accumulation_buffer2.resize(size);
        this.reset_stable_frames();
    }
    // http://en.wikipedia.org/wiki/Halton_sequence
    static halton_seq(prime, index = 1 /* NOT! zero-based */) {
        let r = 0.0;
        let f = 1.0;
        let i = index;
        while (i > 0) {
            f /= prime;
            r += f * (i % prime);
            i = Math.floor(i / prime);
        }
        return r;
    }
    static initialize_halton_2_3(seq, samples_count) {
        for (let i = 0; i < samples_count; i++) {
            const u = (AccumulationBuffer.halton_seq(2, i + 1) - 0.5) * 2.0;
            const v = (AccumulationBuffer.halton_seq(3, i + 1) - 0.5) * 2.0;
            seq.push({ x: u, y: v });
        }
    }
    static get_jitter_samples(sample_type, halton_count) {
        const samples = new Array();
        switch (sample_type) {
            case JitterSampleType.helix_4:
                samples.push({ x: -0.25, y: -0.25 }); // ll  3  1
                samples.push({ x: 0.25, y: 0.25 }); // ur   \/|
                samples.push({ x: 0.25, y: -0.25 }); // lr   /\|
                samples.push({ x: 0.25, y: 0.25 }); // ul  0  2
                break;
            case JitterSampleType.halton_2_3_16:
                AccumulationBuffer.initialize_halton_2_3(samples, halton_count);
                break;
        }
        return samples;
    }
    jitter(viewport_width, viewport_height, engine_camera) {
        if (this._accumulation_frame_count <= 1) {
            engine_camera.set_jitter({ x: 0, y: 0 });
            this._jitter_index = 0;
            return;
        }
        const sample_offset = this._jitter_samples[this._jitter_index];
        engine_camera.set_jitter({
            x: sample_offset.x / viewport_width,
            y: sample_offset.y / viewport_height
        });
        if (this._accumulation_index >= 0) {
            // Only progress jitter index in case we've started the progressive rendering to prevent subpixel flickering.
            this._jitter_index = (this._jitter_index + 1) % this._jitter_samples.length;
        }
    }
    reset_stable_frames() {
        this._jitter_index = 0;
        this._accumulation_index = Math.min(this._accumulation_index, Math.min(0, -this._queued_warmup_frames));
        this._queued_warmup_frames = 0;
    }
    increase_stable_frames() {
        this._accumulation_index++;
    }
    queue_warmup_frame_count(warmup_frame_count) {
        if (this._queued_warmup_frames === warmup_frame_count)
            return;
        this._queued_warmup_frames = warmup_frame_count;
    }
    set_accumulation_frame_count(frame_count) {
        if (this._accumulation_frame_count === frame_count) {
            // No change, early out.
            return;
        }
        this._accumulation_frame_count = frame_count;
        // Recompute TAA jitter sequence
        this._jitter_samples = AccumulationBuffer.get_jitter_samples(JitterSampleType.halton_2_3_16, this._accumulation_frame_count);
        this.reset_stable_frames();
    }
    get_accumulation_frame_count() {
        return this._accumulation_frame_count;
    }
    keep_rendering() {
        return this._accumulation_index < this._accumulation_frame_count;
    }
    dispose() {
        this._shader.dispose();
        this.accumulation_buffer1.dispose();
        this.accumulation_buffer2.dispose();
    }
}
//# sourceMappingURL=Accumulator.js.map